import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar, Clock, User, Eye, ChevronDown, ChevronUp, CreditCard, BookOpen, UserCheck, Settings, Clipboard, Tag, AlertCircle, RefreshCw, ArrowUp, ArrowDown, X, Check, Star, Pencil, Copy } from 'lucide-react';
import './ActivityLogs.css';
import Select from 'react-select';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiSearch, FiEye, FiChevronDown, FiFilter, FiPlus, FiRefreshCw } from 'react-icons/fi';

const ActivityLogs = ({ userId }) => {
  const location = useLocation();
  const playerUserId = location.state?.playerUserId;
  const [activityData, setActivityData] = useState([]);
  const userIdToUse = playerUserId || userId;
  const [loading, setLoading] = useState(true);
  const today = new Date();
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(today.getMonth() - 3);
  const [startDate, setStartDate] = useState(threeMonthsAgo.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);
  const [actionFilter, setActionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const modalRef = useRef(null);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL_ACTIVITY_LOGS}?userId=${userIdToUse}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activity logs');
      }

      const data = await response.json();

      setActivityData(
        (data.data?.events || []).map((event, index) => ({
          id: index + 1,
          name: event.name || 'N/A',
          action: event.action || '',
          doneBy: event.doneBy || 'System',
          datetime: event.createdAt || null,
          details: event.details || '',
          type: event.type?.toLowerCase() || '',
          status: event.status?.toLowerCase() || '',
          payloadJson: event.payloadJson ? JSON.parse(event.payloadJson) : null
        }))
      );
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityLogs();
  }, [playerUserId]);

  const filteredData = useMemo(() => {
    return activityData.filter(item => {
      const itemDateISO = new Date(item.datetime).toISOString().split('T')[0];
      const matchesDate = itemDateISO >= startDate && itemDateISO <= endDate;

      const fullText = [
        item.action,
        item.details,
        item.doneBy,
        item.status,
        new Date(item.datetime).toLocaleDateString(),
        new Date(item.datetime).toLocaleTimeString()
      ].join(' ').toLowerCase();

      const searchKeywords = actionFilter.toLowerCase().split(' ').filter(Boolean);
      const matchesSearch =
        searchKeywords.length === 0 || searchKeywords.some(keyword => fullText.includes(keyword));

      const matchesStatus = !statusFilter || item.status === statusFilter;
      const matchesType = !typeFilter || item.type === typeFilter;

      return matchesDate && matchesSearch && matchesStatus && matchesType;
    }).sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime());
  }, [startDate, endDate, actionFilter, statusFilter, typeFilter, activityData]);

  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    return {
      date: date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      })
    };
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    
    navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text, null, 2))
      .then(() => {
        console.log('Copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const openModal = (task) => {
    setSelectedTask({
      ...task,
      Date: formatDateTime(task.datetime).date,
      time: formatDateTime(task.datetime).time,
      username: task.doneBy,
      role: 'User', 
      section: 'COACHING',
      subsection: task.payloadJson?.subSection || 'N/A',
      affectedUserName: task.name,
      affectedId: task.payloadJson?.affected_id || null
    });
    setModalOpen(true);
  };

  const exportToPDF = (data, options = {}) => {
    try {
      if (!data || !Array.isArray(data) || data.length === 0) {
        throw new Error('Invalid or empty data provided');
      }

      const config = {
        title: options.title || 'Activity Logs Report',
        filename: options.filename || 'activity-logs.pdf',
        orientation: options.orientation || 'portrait',
        format: options.format || 'a4',
        ...options
      };

      const doc = new jsPDF({
        orientation: config.orientation,
        unit: 'mm',
        format: config.format
      });

      const addHeader = () => {
        doc.setFontSize(18);
        doc.setFont(undefined, 'bold');
        doc.text(config.title, 14, 20);

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const generatedDate = new Date().toLocaleString('en-US', {
          dateStyle: 'full',
          timeStyle: 'short'
        });
        doc.text(`Generated on: ${generatedDate}`, 14, 28);

        doc.text(`Total Records: ${data.length}`, 14, 34);
      };

      addHeader();

      const processRowData = (item, index) => {
        try {
          const dateObj = item.datetime ? new Date(item.datetime) : null;

          let date = 'N/A';
          let time = 'N/A';

          if (dateObj && !isNaN(dateObj.getTime())) {
            date = dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });

            time = dateObj.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            });
          }

          return [
            item.name || 'N/A',
            date,
            time,
            item.action || 'N/A',
            item.doneBy || 'N/A',
            item.details || 'N/A'
          ];
        } catch (error) {
          console.warn(`Error processing row ${index + 1}:`, error);
          return ['Error', 'Error', 'Error', 'Error', 'Error', 'Error processing data'];
        }
      };

      const tableData = data.map(processRowData);

      const tableConfig = {
        startY: 42,
        head: [['User', 'Date', 'Time', 'Action', 'Done By', 'Details']],
        body: tableData,

        styles: {
          fontSize: 9,
          cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
          valign: 'top',
          overflow: 'linebreak',
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },

        columnStyles: {
          0: { cellWidth: 25, halign: 'left' },   
          1: { cellWidth: 22, halign: 'center' }, 
          2: { cellWidth: 20, halign: 'center' }, 
          3: { cellWidth: 25, halign: 'left' },  
          4: { cellWidth: 22, halign: 'left' },
          5: { cellWidth: 'auto', halign: 'left' } 
        },

        headStyles: {
          fillColor: [52, 152, 219], 
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
          fontSize: 10
        },

        alternateRowStyles: {
          fillColor: [248, 249, 250]
        },

        theme: 'striped',
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.1,

        showHead: 'everyPage',
        pageBreak: 'auto',

        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageSize = doc.internal.pageSize;

          doc.setFontSize(8);
          doc.setTextColor(128, 128, 128);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            pageSize.width - 30,
            pageSize.height - 10,
            { align: 'right' }
          );
        },

        margin: { top: 42, right: 14, bottom: 20, left: 14 }
      };

      autoTable(doc, tableConfig);

      const addFooter = () => {
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height;

        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
      };

      addFooter();

      doc.save(config.filename);

      return {
        success: true,
        filename: config.filename,
        recordCount: data.length,
        message: `PDF exported successfully with ${data.length} records`
      };

    } catch (error) {
      console.error('PDF Export Error:', error);

      alert(`Failed to export PDF: ${error.message}`);

      return {
        success: false,
        error: error.message,
        message: 'PDF export failed'
      };
    }
  };

  const getActionTypeColor = (type) => {
    const colors = {
      trial_creation: '#1e40af',
      batch_change: '#7c3aed',
      coaching_enrollment: '#047857',
      playing_enrollment: '#047857',
      leave: '#d97706',
      payment: '#0f766e',
      discount: '#be185d',
      feedback: '#0ea5e9',
      coaching_plan_change: '#c2410c',
      plan_renewal: '#0d9488',
      dropped: '#991b1b',
    };
    return colors[type] || '#6b7280';
  };

  const getActionIcon = (type) => {
    const icons = {
      trial_creation: <Calendar size={20} />,
      batch_change: <Settings size={20} />,
      coaching_enrollment: <CreditCard size={20} />,
      playing_enrollment: <CreditCard size={20} />,
      leave: <Clipboard size={20} />,
      payment: <CreditCard size={20} />,
      discount: <Tag size={20} />,
      feedback: <Star size={20} />,
      coaching_plan_change: <ArrowUp size={20} />,
      plan_renewal: <RefreshCw size={20} />,
      dropped: <X size={20} />,
    };
    return icons[type] || <BookOpen size={20} />;
  };

  const getStatusStyle = (status) => {
    const styles = {
      scheduled: { backgroundColor: '#fef3c7', color: '#92400e' },
      completed: { backgroundColor: '#dcfce7', color: '#166534' },
      pending: { backgroundColor: '#fef9c3', color: '#854d0e' },
      approved: { backgroundColor: '#d1fae5', color: '#065f46' },
      cancelled: { backgroundColor: '#e0f2fe', color: '#075985' },
      applied: { backgroundColor: '#ede9fe', color: '#5b21b6' },
      revoked: { backgroundColor: '#fee2e2', color: '#991b1b' },
      received: { backgroundColor: '#ecfccb', color: '#3f6212' },
      partial: { backgroundColor: '#cffafe', color: '#155e75' },
      active: { backgroundColor: '#f3e8ff', color: '#7c3aed' },
      failed: { backgroundColor: '#fee2e2', color: '#dc2626' }
    };
    return styles[status] || { backgroundColor: '#f3f4f6', color: '#374151' };
  };

  const uniqueTypeValues = Array.from(
    new Set(activityData.map(item => item.type))
  );

  const uniqueTypes = uniqueTypeValues.map(type => ({
    value: type,
    label: type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }));

  return (
    <div className="enrollment-container">
      {modalOpen && selectedTask && (
        <div
          className="tt-modal-overlay"
          onClick={() => setModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tt-modal-title"
          aria-describedby="tt-modal-desc"
        >
          <div
            className="tt-modal-content"
            onClick={(e) => e.stopPropagation()}
            ref={modalRef}
          >
            {/* Header */}
            <div className="tt-modal-header">
              <h2 className="tt-modal-title" id="tt-modal-title">Change Details</h2>

              <span
                className={`tt-chip ${
                  selectedTask.action === 'created'
                    ? 'success'
                    : selectedTask.action === 'deleted'
                    ? 'danger'
                    : selectedTask.action === 'updated'
                    ? 'warning'
                    : 'info'
                }`}
                title={`Action: ${selectedTask.action}`}
              >
                <span className="dot"></span>
                {selectedTask.action}
              </span>

              <span className="tt-chip" title={`${selectedTask.Date} • ${selectedTask.time}`}>
                🗓 {selectedTask.Date} • {selectedTask.time}
              </span>

              {(selectedTask.affectedUserName || selectedTask.affectedId) && (
                <button
                  type="button"
                  className="tt-chip clickable"
                  onClick={() => copyToClipboard(selectedTask.affectedId)}
                  title="Click to copy affected ID"
                >
                  🧩 {selectedTask.affectedUserName || 'Affected'} — {selectedTask.affectedId ?? 'N/A'}
                </button>
              )}
            </div>

            {/* Body */}
            <div className="tt-modal-body" id="tt-modal-desc">
              {/* Key details grid */}
              <div className="tt-kv-grid">
                <div className="tt-kv">
                  <div className="k">Username</div>
                  <div className="v">{selectedTask.username}</div>
                </div>
                <div className="tt-kv">
                  <div className="k">Role</div>
                  <div className="v">{selectedTask.role}</div>
                </div>
                <div className="tt-kv">
                  <div className="k">Section</div>
                  <div className="v">{selectedTask.section}</div>
                </div>
                <div className="tt-kv">
                  <div className="k">Subsection</div>
                  <div className="v">{selectedTask.subsection}</div>
                </div>
              </div>

              {/* Payload / Diff */}
              {selectedTask.payloadJson && selectedTask.payloadJson.old && selectedTask.payloadJson.new && (
                <>
                  <div className="tt-section-title">Before vs After</div>
                  <div className="diff-toolbar">
                    <button
                      className="tt-btn"
                      onClick={() => copyToClipboard(JSON.stringify(selectedTask.payloadJson.old, null, 2))}
                    >
                      <Copy size={14} /> Copy "Before"
                    </button>
                    <button
                      className="tt-btn"
                      onClick={() => copyToClipboard(JSON.stringify(selectedTask.payloadJson.new, null, 2))}
                    >
                      <Copy size={14} /> Copy "After"
                    </button>
                  </div>

                  <div className="diff-split-view">
                    {/* OLD JSON */}
                    <div className="diff-panel">
                      <h4>Before</h4>
                      <pre className="diff-json">
                        {Object.keys(selectedTask.payloadJson.old).map((key) => {
                          let beforeValue = selectedTask.payloadJson.old[key];
                          let afterValue = selectedTask.payloadJson.new[key];
                          const changed = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);
                          if (typeof beforeValue === "object") {
                            beforeValue = JSON.stringify(beforeValue, null, 2);
                          }
                          return (
                            <div key={key} className={`diff-line ${changed ? "removed" : ""}`}>
                              <span className="key">"{key}"</span>: <span>"{beforeValue}"</span>
                            </div>
                          );
                        })}
                      </pre>
                    </div>

                    {/* NEW JSON */}
                    <div className="diff-panel">
                      <h4>After</h4>
                      <pre className="diff-json">
                        {Object.keys(selectedTask.payloadJson.new).map((key) => {
                          let beforeValue = selectedTask.payloadJson.old[key];
                          let afterValue = selectedTask.payloadJson.new[key];
                          const changed = JSON.stringify(beforeValue) !== JSON.stringify(afterValue);
                          if (typeof afterValue === "object") {
                            afterValue = JSON.stringify(afterValue, null, 2);
                          }
                          return (
                            <div key={key} className={`diff-line ${changed ? "added" : ""}`}>
                              <span className="key">"{key}"</span>: <span>"{afterValue}"</span>
                            </div>
                          );
                        })}
                      </pre>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="tt-modal-footer">
              <button className="tt-modal-close-btn" onClick={() => setModalOpen(false)} data-autofocus>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="sales-filters-login" style={{ gap: '30px' }}>
        <div className="floating-select">
          <Select
            id="typeFilter"
            className={`sales-filter-court-booking ${typeFilter ? "has-value" : ""}`}
            classNamePrefix="react-select"
            options={uniqueTypes}
            value={
              typeFilter
                ? { value: typeFilter, label: typeFilter.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') }
                : null
            }
            onChange={(selectedOption) => setTypeFilter(selectedOption?.value || '')}
            placeholder="Event Type"
            isClearable
            menuPortalTarget={document.body}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
          {typeFilter && <label htmlFor="typeFilter">Event Type</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            id="startDate"
            className={`sales-filter-academy ${startDate ? "has-value" : ""}`}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          {startDate && <label htmlFor="startDate">Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            id="endDate"
            className={`sales-filter-academy ${endDate ? "has-value" : ""}`}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
          />
          {endDate && <label htmlFor="endDate">End Date</label>}
        </div>

        <div className="floating-select">
          <button className="tt-action-btn" style={{ marginTop: '1px' }} onClick={fetchActivityLogs}>
            <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          </button>
        </div>

      </div>

      <div className="timeline-container">
        <div className="search-bar" style={{ margin: '0px' }}>
          <div className="input-container" style={{ margin: '0px', padding: '0px' }}>
            <input
              type="text"
              id="activityFilter"
              placeholder=""
              className={`search-input ${actionFilter ? "has-value" : ""}`}
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            />
            <label htmlFor="activityFilter" className="floating-label">🔍 Search</label>
          </div>

          <button
            onClick={() => exportToPDF(filteredData)}
            className="create-button"
          >
            Export PDF
          </button>
        </div>

        <div className="timeline-content">
          {loading ? (
            <div className="premium-loader">
              <div className="progress-track">
                <div className="progress-bar"></div>
              </div>
              <div className="status-text">
                <span>L</span>
                <span>O</span>
                <span>A</span>
                <span>D</span>
                <span>I</span>
                <span>N</span>
                <span>G</span>
              </div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="no-activities">
              <Clock size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p>No activities found for the selected criteria</p>
            </div>
          ) : (
            <div className="timeline">
              <div className="timeline-line"></div>

              {Object.entries(
                filteredData.reduce((groups, item) => {
                  const date = formatDateTime(item.datetime).date;
                  if (!groups[date]) {
                    groups[date] = [];
                  }
                  groups[date].push(item);
                  return groups;
                }, {})
              ).map(([date, activities]) => (
                <div key={date} className="date-group">
                  <div className="date-header">
                    <div className="date-title">{date}</div>
                    <div className="date-meta">
                      <span className="date-count">{activities.length} {activities.length === 1 ? 'activity' : 'activities'}</span>
                    </div>
                  </div>

                  {activities.map((item) => {
                    const { time } = formatDateTime(item.datetime);
                    const statusStyle = getStatusStyle(item.status);

                    return (
                      <div key={item.id} className="timeline-item">
                        <div
                          className="timeline-dot"
                          style={{ backgroundColor: getActionTypeColor(item.type) }}
                        ></div>

                        <div
                          className="activity-card"
                          style={{ borderLeft: `4px solid ${getActionTypeColor(item.type)}` }}
                        >
                          <div className="activity-header">
                            <div className="activity-main">
                              <div className="activity-title-row" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                                <div
                                  className="activity-icon"
                                  style={{ backgroundColor: getActionTypeColor(item.type) }}
                                >
                                  {getActionIcon(item.type)}
                                </div>
                                <h3 className="activity-title">
                                  {item.action} <span className="by-text">
                                    by&nbsp;
                                    <span className="done-by">{item.doneBy}</span>
                                  </span>
                                </h3>
                                {item.status && item.status !== 'completed' && (
                                  <span className="status-badge-logs" style={statusStyle}>
                                    {item.status}
                                  </span>
                                )}
                              </div>

                              <div className="activity-meta">
                                <div className="meta-item">
                                  <User size={16} />
                                  <span className="meta-user">{item.name}</span>
                                </div>
                                <div className="meta-item">
                                  <Clock size={16} />
                                  <span>{time}</span>
                                </div>

                              </div>

                              <div className="activity-details">
                                <div className="details-grid">
                                  <div className="detail-row full-width">
                                    <span className="detail-value">{item.details}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {item.payloadJson && (
                                <div className="payload-button-container">
                                  <button 
                                    className="view-payload-button"
                                    onClick={() => openModal(item)}
                                  >
                                    <Eye size={16} />
                                    View Changes
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ActivityLogs;